/* Offer new site code without interrupting work or reloading automatically. */
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else root.LouderrAppUpdate = api;
})(globalThis, function () {
  "use strict";
  function create({ assets, fetch, safe, render, reload, now = Date.now,
    setTimer = setTimeout, clearTimer = clearTimeout, Controller = AbortController }) {
    const interval = 5 * 60 * 1000;
    let last = -Infinity, pending = null, dismissed = null, flight = null, epoch = 0;
    let visible = true, online = true;
    const valid = assets.length === 2 && assets.every(a =>
      /^\/static\/startup\.(js|css)$/.test(a.path) && /^[a-f0-9]{12}$/.test(a.version));
    function canRefresh() { try { return Boolean(safe()); } catch (_) { return false; } }
    function present() {
      render(Boolean(pending && pending !== dismissed && visible && online && canRefresh()));
    }
    async function check() {
      present();
      if (!valid || !visible || !online || flight || now() - last < interval) return;
      last = now();
      const generation = epoch, controller = new Controller();
      flight = controller;
      const deadline = setTimer(() => controller.abort(), 8000);
      try {
        const versions = await Promise.all(assets.map(async asset => {
          const response = await fetch(`${asset.path}.br.json?check=${last}`, {
            cache: "no-store", credentials: "omit", redirect: "error", signal: controller.signal,
          });
          if (!response.ok) throw new Error("Version unavailable");
          const text = await response.text();
          if (text.length > 2048) throw new Error("Invalid version metadata");
          const data = JSON.parse(text), hash = data?.source?.sha256;
          if (data.version !== 1 || data.encoding !== "br" || !/^[a-f0-9]{64}$/.test(hash || "")) {
            throw new Error("Invalid version metadata");
          }
          return hash.slice(0, 12);
        }));
        if (generation !== epoch || controller.signal.aborted) return;
        pending = versions.some((v, i) => v !== assets[i].version) ? versions.join(":") : null;
        present();
      } catch (_) { controller.abort(); /* Failed probes never disrupt browsing. */ }
      finally {
        clearTimer(deadline);
        if (flight === controller) flight = null;
      }
    }
    return {
      check, present,
      availability(isVisible, isOnline) {
        visible = isVisible; online = isOnline;
        if (!visible || !online) { epoch += 1; flight?.abort(); flight = null; }
        present();
      },
      dismiss() { dismissed = pending; present(); },
      refresh() {
        if (!pending || !visible || !online || !canRefresh()) { present(); return false; }
        // Only this explicit user action may navigate. Native unload guards remain in force.
        reload(); return true;
      },
    };
  }

  function connect({ document: doc, window: win, safe }) {
    const assets = [
      doc.querySelector('script[src^="/static/startup.js?"]')?.getAttribute("src"),
      doc.querySelector('link[rel="stylesheet"][href^="/static/startup.css?"]')?.getAttribute("href"),
    ].filter(Boolean).map(value => {
      const url = new URL(value, win.location.href);
      return { path: url.pathname, version: url.searchParams.get("v") };
    });
    const notice = doc.createElement("aside");
    notice.className = "app-update-notice"; notice.hidden = true;
    notice.setAttribute("aria-label", "Website update");
    const text = doc.createElement("p"); text.textContent = "A new version of LOUDERR is ready.";
    text.setAttribute("role", "status");
    const refresh = doc.createElement("button"), later = doc.createElement("button");
    refresh.type = later.type = "button";
    refresh.className = "publish-button"; later.className = "text-button";
    refresh.textContent = "Refresh"; later.textContent = "Later";
    notice.append(text, refresh, later); doc.body.append(notice);
    const control = create({ assets, fetch: win.fetch.bind(win), safe,
      render: shown => { if (notice.hidden === shown) notice.hidden = !shown; }, reload: () => win.location.reload() });
    refresh.addEventListener("click", () => control.refresh());
    later.addEventListener("click", () => control.dismiss());
    let timer = null;
    const availability = () => control.availability(doc.visibilityState === "visible", win.navigator.onLine !== false);
    function schedule(delay = 5 * 60 * 1000) {
      win.clearTimeout(timer);
      if (doc.visibilityState === "visible") timer = win.setTimeout(async () => { await control.check(); schedule(); }, delay);
    }
    function resume() { availability(); void control.check(); schedule(); }
    doc.addEventListener("visibilitychange", resume);
    win.addEventListener("online", resume);
    win.addEventListener("offline", availability);
    win.addEventListener("pagehide", () => { win.clearTimeout(timer); control.availability(false, false); });
    win.addEventListener("pageshow", resume);
    // Recheck safety after handlers have opened a modal or changed a draft, not before them.
    for (const type of ["click", "input", "change", "close", "focusout"]) {
      doc.addEventListener(type, () => queueMicrotask(() => control.present()), true);
    }
    const observer = new MutationObserver(() => control.present());
    observer.observe(doc.body, { subtree: true, attributes: true, attributeFilter: ["open", "hidden", "data-view"] });
    availability(); schedule(30000);
    return control;
  }
  return { create, connect };
});
